<template>
  <div>
    <div class="container">
      <div class="container">

        <!-- PAGE HEADER -->
        <div class="section-head" :style="{ '--accent': '#4F7EB3' }">
          <div class="head-left">
            <h3 class="title">{{ $t('streams.external.connectTitle') }}</h3>
          </div>
          <div class="head-actions">
            <button type="button" class="btn-outline" @click="cancelConnectExternalData">
              <i class="bi bi-x-square"></i>
              {{ $t('cameras.actions.cancel') }}
            </button>
          </div>
        </div>

        <!-- FORM CARD -->
        <div class="card form-card kpi-accent" :style="{ '--accent': '#4F7EB3' }">
          <div class="card-header form-head">
            <h4 class="form-title">
              {{ APITypeName ? APIName || $t('streams.external.connection') : $t('streams.external.connection') }}
            </h4>
            <span v-if="APITypeName" class="type-chip">
              <i class="bi bi-plug"></i> {{ APITypeName }}
            </span>
          </div>

          <div class="card-body">
            <div class="form-wrap">
              <!-- Name -->
              <div class="form-row">
                <label class="form-label" for="name">{{ $t('streams.external.connectionName') }}</label>
                <input
                  id="name"
                  type="text"
                  class="input"
                  v-model="APIName"
                  :placeholder="$t('streams.external.namePlaceholder')"
                />
              </div>

              <!-- Type -->
              <div class="form-row">
                <label class="form-label">{{ $t('streams.external.chooseType') }}</label>
                <dropdown-api-type :api_type_name="APITypeName" @apitype="on_apitype" />
              </div>

              <!-- Weather-only fields -->
              <div v-if="APITypeName === 'Weather Data'" class="group">
                <div class="group-head">
                  <i class="bi bi-geo-alt"></i>
                  <span>{{ $t('streams.external.location') }}</span>
                </div>

                <div class="grid-2">
                  <div class="form-row">
                    <label class="form-label sub">{{ $t('streams.external.latitude') }}</label>
                    <input
                      type="number"
                      class="input"
                      v-model="Latitude"
                      name="latitude"
                      id="latitude"
                      step="0.0001"
                      min="-90"
                      max="90"
                      placeholder="50.8503"
                    />
                  </div>

                  <div class="form-row">
                    <label class="form-label sub">{{ $t('streams.external.longitude') }}</label>
                    <input
                      type="number"
                      class="input"
                      v-model="Longitude"
                      name="longitude"
                      id="longitude"
                      step="0.0001"
                      min="-180"
                      max="180"
                      placeholder="4.3517"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- FOOTER ACTIONS -->
          <div class="form-actions">
            <div v-if="isSaving" class="saving">
              <div class="saver"></div>
              <span>{{ $t('cameras.actions.saving') }}</span>
            </div>

            <button
              type="button"
              class="btn-solid ok"
              :class="{ 'is-disabled': isSaveButtonDisabled }"
              @click="createExternalDataConnection"
              :disabled="isSaveButtonDisabled"
            >
              <i class="bi bi-save"></i> {{ $t('cameras.actions.save') }}
            </button>

            <button type="button" class="btn-outline" @click="cancelConnectExternalData">
              <i class="bi bi-x-square"></i> {{ $t('cameras.actions.cancel') }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

import { useAPIWeatherDataStore } from '@/stores/APIWeatherDataStore'

import DropdownApiType from './DropdownAPIType.vue'

export default {
    components: {
        DropdownApiType,
    },

    setup(){
        const toast = useToast()
        const { t } = useI18n()

        const apiweatherdataStore = useAPIWeatherDataStore()

        const router = useRouter()
        const route = useRoute()

        const APIName = ref('')
        const APITypeName = ref('')
        const APITypeId = ref('')
        
        const Latitude = ref(0)
        const Longitude = ref(0)
        
        const UserId = ref('')

        const isSaving = ref(null)

        onMounted(() => {
            if (route.query.type) {
                APITypeName.value = route.query.type
                console.log(APITypeName.value)
            }
        })

        //Update values when values in child are changed
        const on_apitype = (value) => {
            APITypeName.value = value
        }

        const resetValues = () => {
            APITypeName.value = ''
            APITypeId.value = ''
            Latitude.value = 0
            Longitude.value = 0
        }
        
        const isSaveButtonDisabled = computed (() => {
            return (
                !APIName.value ||
                APITypeName.value === ''
            )
        })

        const createExternalDataConnection = async () => {
            try{
                if (APITypeName.value === 'Weather Data') {
                    isSaving.value = true
                    const createdAPIWeatherDataRecord = await apiweatherdataStore.createAPIWeatherDataRecord(APIName.value, APITypeName.value, Latitude.value, Longitude.value)
                    
                }
                
                resetValues()
                toast.success(t('streams.external.added'))
                router.push('/external_data')
                await apiweatherdataStore.fetchAPIWeatherDataRecords()
                isSaving.value = false
            } catch (error) {
                isSaving.value = false
                console.error("An unexpected error occurred while writing data to the database:", error)
                toast.error(t('cameras.saveError'), error)
            }
        }

        const cancelConnectExternalData = () => {
            if (confirm(t('streams.external.confirmCancel'))) {
                resetValues()
                setTimeout(() => {
                    router.push('/external_data')
                }, 100) // Add a slight delay before navigation
            }
        }

        return {
            APIName,
            APITypeName,
            APITypeId,
            Latitude,
            Longitude,
            UserId,
            apiweatherdataStore,
            createExternalDataConnection,
            on_apitype,
            cancelConnectExternalData,
            isSaveButtonDisabled,
            isSaving,
        }
    }
}
</script>

<style scoped>
/* ---------- Header ---------- */
.section-head{
  display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;
  border-left:6px solid var(--accent,#4F7EB3);
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  padding:12px 14px; margin:10px 0 14px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
}
.title{ margin:0; font-weight:800; color:#425461 }
.head-actions{ display:flex; gap:10px; flex-wrap:wrap }

/* ---------- Buttons ---------- */
.btn-outline{
  display:inline-flex; align-items:center; gap:8px;
  border:1px solid #4F7EB3; color:#4F7EB3; background:#fff;
  padding:8px 12px; border-radius:10px; cursor:pointer; text-decoration:none;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }

.btn-solid{
  display:inline-flex; align-items:center; gap:8px;
  border:0; color:#fff; padding:8px 14px; border-radius:10px; cursor:pointer;
}
.btn-solid.ok{ background:#6C9046 }
.btn-solid.ok.is-disabled,
.btn-solid.ok:disabled{ opacity:.6; cursor:not-allowed }

/* ---------- Form Card ---------- */
.form-card{ border:none; box-shadow:0 4px 18px rgba(13,38,76,.06); border-radius:14px }
.kpi-accent{ position:relative }
.kpi-accent::before{
  content:""; position:absolute; inset:0 auto 0 0; width:6px; background:var(--accent,#4F7EB3);
  border-radius:14px 0 0 14px;
}

.form-head{
  display:flex; align-items:center; justify-content:space-between; gap:8px; padding:12px 16px;
  border-bottom:1px solid #eef3f7;
}
.form-title{ margin:0; color:#4F7EB3; font-weight:800 }

.type-chip{
  display:inline-flex; align-items:center; gap:6px; padding:6px 10px;
  background:#f7fbff; color:#4F7EB3; border:1px solid #e6edf3; border-radius:999px;
  font-size:.84rem; font-weight:700;
}

.card-body{ padding:14px 16px }

/* ---------- Form Layout ---------- */
.form-wrap{ max-width:760px }
.form-row{ display:flex; flex-direction:column; gap:6px; margin-bottom:14px }
.form-label{ color:#425461; font-weight:700 }
.form-label.sub{ color:#4F7EB3; font-weight:700 }

.input{
  width:100%; border:1px solid #dfe7ee; border-radius:10px; padding:10px 12px;
  background:#fff; color:#2b3a44; outline:none;
}
.input:focus{ border-color:#4F7EB3; box-shadow:0 0 0 3px rgba(79,126,179,.12) }

/* Group (for weather) */
.group{ margin-top:6px; border:1px solid #eef3f7; border-radius:12px; padding:12px }
.group-head{ display:flex; align-items:center; gap:8px; color:#4F7EB3; font-weight:800; margin-bottom:10px }

.grid-2{
  display:grid; grid-template-columns:1fr 1fr; gap:16px;
}
@media (max-width: 720px){ .grid-2{ grid-template-columns:1fr } }

/* ---------- Footer ---------- */
.form-actions{
  display:flex; align-items:center; gap:10px; justify-content:flex-end;
  padding:12px 16px; border-top:1px solid #eef3f7;
}
.saving{ display:flex; align-items:center; gap:8px; margin-right:auto; color:#6b7a86 }

/* ---------- Loader (saving) ---------- */
.saver{
  width:22px; padding:2px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }
</style>